import { BOOKING_STAGE_LIST_CODE } from '../types/booking.js';
/**
 * Every type a settings list's items can be, in the order Settings offers
 * them. The type decides the value's input and how it is checked; every item
 * also carries a label, which is what screens show.
 */
export const SETTING_LIST_ITEM_TYPES = [
    {
        type: 'TEXT',
        label: 'Text',
        description: 'A name, stored under a short code',
        valueLabel: 'Code',
    },
    { type: 'NUMBER', label: 'Number', description: 'A number, with a label', valueLabel: 'Number' },
    { type: 'DATE', label: 'Date', description: 'A day, with a label', valueLabel: 'Date' },
    { type: 'COLOR', label: 'Colour', description: 'A colour, with a name', valueLabel: 'Colour' },
    { type: 'URL', label: 'Link', description: 'A web address, with a name', valueLabel: 'Address' },
    {
        type: 'EMAIL',
        label: 'Email',
        description: 'An email address, with a name',
        valueLabel: 'Email',
    },
    { type: 'USER', label: 'Person', description: 'Somebody in the directory', valueLabel: 'Person' },
    { type: 'VENDOR', label: 'Vendor', description: 'A vendor', valueLabel: 'Vendor' },
    { type: 'VENUE', label: 'Venue', description: 'A venue', valueLabel: 'Venue' },
];
/** The spec of a list's item type; an unknown type is read as text. */
export function settingListItemTypeSpec(type) {
    return SETTING_LIST_ITEM_TYPES.find((s) => s.type === type) ?? SETTING_LIST_ITEM_TYPES[0];
}
/** Whether an item's value is a record picked from the directory. */
export function isEntityItemType(type) {
    return type === 'USER' || type === 'VENDOR' || type === 'VENUE';
}
/**
 * Lists the code reads more from than a value and a label — a booking stage's
 * kind decides whether a vendor reads as booked, a meal's whether it is a
 * kids' meal. Their items get these fields in the item form; every other key
 * an item carries is kept as it is.
 */
export const SETTING_LIST_EXTRA_FIELDS = {
    [BOOKING_STAGE_LIST_CODE]: [
        {
            key: 'kind',
            label: 'Counts as',
            kind: 'select',
            options: [
                { value: 'open', label: 'Still open' },
                { value: 'booked', label: 'Booked' },
                { value: 'declined', label: 'Declined' },
            ],
            defaultValue: 'booked',
            help: 'Decides whether a vendor at this stage reads as booked on the Vendors view',
        },
    ],
    meal_options: [
        { key: 'isKidsMeal', label: "Kids' meal", kind: 'boolean', defaultValue: false },
    ],
};
export function settingListExtraFields(listCode) {
    return Object.prototype.hasOwnProperty.call(SETTING_LIST_EXTRA_FIELDS, listCode)
        ? SETTING_LIST_EXTRA_FIELDS[listCode]
        : [];
}
function asText(v) {
    if (typeof v === 'string')
        return v;
    if (typeof v === 'number' && Number.isFinite(v))
        return String(v);
    return '';
}
export function readSettingListItem(raw) {
    if (typeof raw === 'string' || typeof raw === 'number') {
        const text = asText(raw);
        return {
            value: text,
            label: text,
            extras: {},
            shape: typeof raw === 'string' ? 'string' : 'number',
            namedByName: false,
        };
    }
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
        return { value: '', label: '', extras: {}, shape: 'object', namedByName: false };
    }
    const obj = raw;
    // A meal option is named by `name` (what its seeding reads first), with or
    // without the `label` Settings writes beside it; both follow the label.
    const namedByName = typeof obj.name === 'string';
    const value = asText(obj.value) || (namedByName ? asText(obj.name) : '');
    const label = asText(obj.label) || (namedByName ? asText(obj.name) : '') || value;
    const extras = {};
    for (const [k, v] of Object.entries(obj)) {
        if (k === 'value' || k === 'label' || (namedByName && k === 'name'))
            continue;
        extras[k] = v;
    }
    return { value, label, extras, shape: 'object', namedByName };
}
/** A new item, with the defaults of any field its list carries. */
export function newSettingListItem(listCode) {
    const extras = {};
    for (const f of settingListExtraFields(listCode))
        extras[f.key] = f.defaultValue;
    return { value: '', label: '', extras, shape: 'object', namedByName: false };
}
/** The item as stored: in the shape it was read from, its value typed for the list. */
export function writeSettingListItem(draft, itemType) {
    const value = draft.value.trim();
    const label = draft.label.trim() || value;
    const typed = itemType === 'NUMBER' && value !== '' ? Number(value) : value;
    const plain = label === value && Object.keys(draft.extras).length === 0;
    if (plain && draft.shape === 'string')
        return value;
    if (plain && draft.shape === 'number' && typeof typed === 'number')
        return typed;
    return {
        ...draft.extras,
        ...(draft.namedByName ? { name: label } : {}),
        value: typed,
        label,
    };
}
// ─── Checks ─────────────────────────────────────────────────────────────────
const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const COLOR_PATTERN = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function isRealDay(text) {
    const m = DATE_PATTERN.exec(text);
    if (!m)
        return false;
    const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
    return d.toISOString().slice(0, 10) === text;
}
function isWebAddress(text) {
    try {
        const url = new URL(text);
        return url.protocol === 'http:' || url.protocol === 'https:';
    }
    catch {
        return false;
    }
}
/** What is wrong with one item's value for its list's type, in words; null when nothing. */
export function settingListValueProblem(type, value) {
    const text = value.trim();
    const spec = settingListItemTypeSpec(type);
    if (!text) {
        return isEntityItemType(type)
            ? `Choose a ${spec.label.toLowerCase()}`
            : `Enter a ${spec.valueLabel.toLowerCase()}`;
    }
    switch (type) {
        case 'NUMBER':
            return Number.isFinite(Number(text)) ? null : 'Enter a number';
        case 'DATE':
            return isRealDay(text) ? null : 'Enter a real date';
        case 'COLOR':
            return COLOR_PATTERN.test(text) ? null : 'Enter a colour like #7c3aed';
        case 'URL':
            return isWebAddress(text) ? null : 'Enter a web address starting with https://';
        case 'EMAIL':
            return EMAIL_PATTERN.test(text) ? null : 'Enter an email address';
        default:
            return null;
    }
}
/**
 * A code for a typed name: lower case, words joined by underscores — what the
 * office's own lists use (`contract_signed`, `kosher_mehadrin`).
 */
export function suggestSettingListValue(label) {
    return label
        .normalize('NFKD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 100);
}
/** The positions of items whose value another item already has (the later ones). */
export function duplicateSettingListValues(drafts) {
    const seen = new Set();
    const dupes = new Set();
    drafts.forEach((d, i) => {
        const key = d.value.trim().toLowerCase();
        if (!key)
            return;
        if (seen.has(key))
            dupes.add(i);
        else
            seen.add(key);
    });
    return dupes;
}
/** A list code: lower case, digits and underscores, starting with a letter. */
export const SETTING_LIST_CODE_PATTERN = /^[a-z][a-z0-9_]*$/;
//# sourceMappingURL=settingListItems.js.map